import React from "react";
import styles from "./collection-card.module.scss";
import classNames from "classnames";
import SkeletonLoader from "@/components/layout/skeleton-loader/skeleton-loader";
import { motion } from "framer-motion";
import RemoveBtn from "@/features/bridge/components/remove-btn/remove-btn";
import type { GeneralTokenCollectionType } from "@/features/bridge/types/open-sea-api.types";
import { Tooltip } from "antd";
import { BiCopy } from "react-icons/bi";
import { getTokenBackgroundColor } from "@/utils/token-color.util";
import { useBridge } from "@/features/bridge/providers/bridge.provider";
import { generateImageFallbacks } from "@/utils/nft-image-fallback.util";
import { useState, useEffect } from "react";

type Props = {
  collectionName: string;
  collectionKey: string; // Collection address (unique identifier)
  activeTokenCollection: string | undefined;
  setActiveCollection: (collection: string) => void;
  collectionAmount: number | undefined;
  selectionCount: number;
  isDisabled: boolean;
  clearTokenCollection: () => void;
  collection: GeneralTokenCollectionType[string];
  tokenType: "NFT" | "MULTI";
};

// Small collection image component with fallback support
const CollectionImage = ({
  src,
  alt,
  identifier,
  symbol,
  contractAddress,
  chainId,
  collectionName,
  tokenType,
  onImageLoad,
  onImageError
}: {
  src?: string;
  alt: string;
  identifier: string;
  symbol?: string;
  contractAddress?: string;
  chainId?: number;
  collectionName?: string;
  tokenType?: string;
  onImageLoad?: () => void;
  onImageError?: () => void;
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(src || null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const generateFallback = async () => {
      if (!src && contractAddress && identifier && chainId) {
        setIsLoading(true);
        try {
          const fallbacks = await generateImageFallbacks(
            src,
            symbol,
            identifier,
            contractAddress,
            chainId,
            tokenType === 'MULTI' ? 'erc1155' : 'erc721'
          );
          if (fallbacks.length > 0) {
            setImageUrl(fallbacks[0]);
          }
        } catch (error) {
          console.warn('Failed to generate collection image fallback:', error);
        }
        setIsLoading(false);
      }
    };

    generateFallback();
  }, [src, symbol, identifier, contractAddress, chainId]);

  const handleError = () => {
    setHasError(true);
    onImageError?.();
  };

  const handleLoad = () => {
    setHasError(false);
    onImageLoad?.();
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!imageUrl || hasError) {
    // Show collection name or symbol as fallback with square aspect ratio
    const text = symbol || collectionName || '?';
    const fontSize = text.length > 8 ? '8px' : text.length > 4 ? '10px' : '12px';

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        aspectRatio: '1',
        fontSize,
        fontWeight: 'bold',
        color: 'white',
        textAlign: 'center',
        padding: '2px',
        wordWrap: 'break-word',
        overflow: 'hidden',
        lineHeight: '1.1'
      }}>
        {text}
      </div>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={imageUrl}
      alt={alt}
      style={{
        objectFit: 'contain',
        width: 'calc(100% - 4px)',
        height: 'calc(100% - 4px)',
        position: 'absolute',
        top: '2px',
        left: '2px'
      }}
      onError={handleError}
      onLoad={handleLoad}
      loading="lazy"
      draggable={false}
    />
  );
};

const CollectionCard = ({
  collectionAmount,
  collectionName,
  collectionKey,
  setActiveCollection,
  activeTokenCollection,
  selectionCount,
  isDisabled,
  clearTokenCollection,
  collection,
  tokenType,
}: Props) => {
  const { bridgeControls } = useBridge();
  const [hasWorkingImage, setHasWorkingImage] = useState(!!collection.collectionData?.image_url);

  // Use centralized color utility based on contract address
  const colorConfig = getTokenBackgroundColor(collection.collectionData.address);

  // Format address to show first 10 and last 8 characters
  const formatAddress = (address: string): string => {
    if (address.length <= 18) return address;
    return `${address.slice(0, 10)}...${address.slice(-8)}`;
  };

  // Copy address to clipboard
  const copyToClipboard = async (address: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    try {
      await navigator.clipboard.writeText(address);
      // Optional: Add toast notification here
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };
  const handleClick = () => {
    setActiveCollection(collectionKey);
  };

  const handleClearCollection = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearTokenCollection();
  };

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={classNames(
        styles.collectionCard,
        selectionCount && styles.containsSelected,
        activeTokenCollection === collectionKey && styles.active,
        isDisabled && styles.disabled,
      )}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={styles.collectionInfo}
      >
        <div
          className={styles.image}
          style={{
            backgroundColor: !hasWorkingImage
              ? colorConfig.backgroundColor
              : undefined
          }}
        >
          <CollectionImage
            src={collection.collectionData?.image_url}
            alt={collection.collectionData?.name || "Collection"}
            identifier={collection.tokenData[0]?.identifier || "1"}
            symbol={collection.tokenData[0]?.symbol}
            contractAddress={collection.collectionData?.address}
            chainId={bridgeControls.fromChain.originChain?.chainId}
            collectionName={collection.collectionData?.name}
            tokenType={tokenType}
            onImageLoad={() => setHasWorkingImage(true)}
            onImageError={() => setHasWorkingImage(false)}
          />
        </div>
        <div className={styles.details}>
          <div className={styles.title}>
            {collection.collectionData?.name || <SkeletonLoader />}
          </div>
          <div className={styles.addressContainer}>
              <span className={styles.address}>
                {collection.collectionData?.address && formatAddress(collection.collectionData.address)}
              </span>
            {collection.collectionData?.address && (
              <button
                className={styles.copyButton}
                onClick={(e) => copyToClipboard(collection.collectionData!.address, e)}
                title="Copy address"
              >
                <BiCopy size={12} />
              </button>
            )}
          </div>
          <div className={styles.subtitle}>
            {tokenType === "NFT" ? "Tokens available:" : "Token types available:"} <b>{collectionAmount}</b>
          </div>
        </div>
        {!!selectionCount && (
          <div className={styles.selectedCount}>
            <RemoveBtn handleFunction={handleClearCollection} />
          </div>
        )}
      </motion.div>
    </button>
  );
};

export default CollectionCard;
